import { Component, OnInit, Input, SimpleChanges, OnChanges, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Alert } from '../../../../../assets/js/com/ts_api_client/model/alert';
import { ALERT_TYPE } from '../../../../bdo/enums/alertType.enum';
import { BdoApiService } from '../../../services/bdo-api.service';
import { TRACKING } from '../../../enums/trackingParts.enum';
import { TrackingService } from '../../../services/tracking.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
    selector: 'bdo-alert-tile',
    templateUrl: './alert-tile.component.html',
    styleUrls: ['./alert-tile.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AlertTileComponent implements OnInit, OnChanges, OnDestroy {
  @Input() alert: Alert = {};
  @Input() accountId: string;
  public TRACKING = TRACKING;
  public routerUrl: string;
  public queryParams: { [key: string]: string } | null = null;
  public newTab = false;
  public alertText: string = '';
  public alertImage: string = '';
  public alertHeadline: string = '';
  public linkText: string;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private trackingService: TrackingService,
    private apiService: BdoApiService,
    private translateService: TranslateService
  ) { }

  ngOnInit() {
    this.setLinkAttributes(this.alert.id);
    this.trackingService.postSimpleTracking(TRACKING.LOCATION.DASHBOARD, TRACKING.ACTION.SHOW, TRACKING.LOCATION.ALERT + ' ' + this.alert.id);
    this.subscriptions.add(this.translateService.onLangChange.subscribe({
      next: () => this.setLinkAttributes(this.alert.id)
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.alert && changes.alert.currentValue) {
      this.setLinkAttributes(this.alert.id);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  setLinkAttributes(alertId: string) {
    const data = this.getAlertData(alertId);
    this.alertText = data.text;
    this.alertImage = data.image;
    this.alertHeadline = data.headline;
    this.routerUrl = data.routerUrl;
    this.queryParams = data.queryParams ?? null;
    this.linkText = data.linkText;
    this.newTab = data.newTab;
  }

  inspectAlert(alert: Alert) {
    this.trackingService.postSimpleTracking(TRACKING.LOCATION.DASHBOARD, TRACKING.ACTION.GOTO + ' ' + TRACKING.LOCATION.ALERT + ' ' + alert.id);

    if (this.alert.id !== ALERT_TYPE.CONFIRM_PERSONAL_DATA) { // Needs to be manually confirmed in personal data
      this.apiService.postAlert(alert).subscribe();
    }
  }

  private getAlertData(alertId: string): {
    text: string;
    image: string;
    headline: string;
    routerUrl: string;
    queryParams?: { [key: string]: string };
    linkText: string; newTab: boolean } {
    switch (alertId) {
      case ALERT_TYPE.DEMAND_OVERDUE:
        return {
          text: this.translateService.instant('dashboard.alertTile.text.DEMAND_OVERDUE'),
          image: 'assets/alert/DEMAND_OVERDUE_880x622px.jpg',
          headline: this.translateService.instant('dashboard.alertTile.title.DEMAND_OVERDUE'),
          routerUrl: '/zahlungen/' + this.accountId,
          linkText: this.translateService.instant('dashboard.alertTile.link.payment'),
          newTab: false,
        };
      case ALERT_TYPE.PAYMENT_REMINDER:
        return {
          text: this.translateService.instant('dashboard.alertTile.text.PAYMENT_REMINDER'),
          image: 'assets/alert/PAYMENT_REMINDER_880x622px.jpg',
          headline: this.translateService.instant('dashboard.alertTile.title.PAYMENT_REMINDER'),
          routerUrl: '/zahlungen/' + this.accountId,
          linkText: this.translateService.instant('dashboard.alertTile.link.payment'),
          newTab: false,
        };
      case ALERT_TYPE.MISSING_MAIL_ADDRESS:
        return {
          text: this.translateService.instant('dashboard.alertTile.text.MISSING_MAIL_ADDRESS'),
          image: 'assets/alert/MISSING_MAIL_ADDRESS_880x622px.jpg',
          headline: this.translateService.instant('dashboard.alertTile.title.MISSING_MAIL_ADDRESS'),
          routerUrl: '/daten/' + this.accountId,
          linkText: this.translateService.instant('dashboard.alertTile.link.data'),
          newTab: false,
        };
      case ALERT_TYPE.MISSING_SEPA:
        return {
          text: this.translateService.instant('dashboard.alertTile.text.MISSING_SEPA'),
          image: 'assets/alert/MISSING_SEPA_880x622px.jpg',
          headline: this.translateService.instant('dashboard.alertTile.title.MISSING_SEPA'),
          routerUrl: '/daten/' + this.accountId,
          linkText: this.translateService.instant('dashboard.alertTile.link.banking'),
          newTab: false,
        };
      case ALERT_TYPE.TOS_CHANGED:
        return {
          text: this.translateService.instant('dashboard.alertTile.text.TOS_CHANGED'),
          image: 'assets/alert/TOS_CHANGED_880x622px.jpg',
          headline: this.translateService.instant('dashboard.alertTile.title.TOS_CHANGED'),
          routerUrl: '/nutzungsbedingungen',
          linkText: this.translateService.instant('dashboard.alertTile.link.details'),
          newTab: true,
        };
      case ALERT_TYPE.CONFIRM_PERSONAL_DATA:
        return {
          text: this.translateService.instant('dashboard.alertTile.text.CONFIRM_PERSONAL_DATA'),
          image: 'assets/alert/CONFIRM_PERSONAL_DATA_880x622px.jpg',
          headline: this.translateService.instant('dashboard.alertTile.title.CONFIRM_PERSONAL_DATA'),
          routerUrl: '/daten/' + this.accountId,
          queryParams: { confirmPersonalData: 'true' },
          linkText: this.translateService.instant('dashboard.alertTile.link.confirmData'),
          newTab: false,
        };
      default:
        return { text: '', image: '', headline: '', routerUrl: '', queryParams: null, linkText: '', newTab: false };
    }
  }

}
